import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, RefreshCw, AlertCircle, CheckCircle, Clock,
  Shield, UserX, AlertTriangle, Eye, Search, Filter,
  ChevronDown, ChevronUp, X, Play, Pause, Ban, 
  UserCheck, FileText, Activity, Users, Store, Lock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { API_URL } from '../../utils/apiConfig';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-900/50 text-emerald-400', icon: CheckCircle },
  under_review: { label: 'Under Review', color: 'bg-amber-900/50 text-amber-400', icon: Eye },
  transactions_frozen: { label: 'Frozen', color: 'bg-blue-900/50 text-blue-400', icon: Pause },
  suspended: { label: 'Suspended', color: 'bg-orange-900/50 text-orange-400', icon: AlertTriangle },
  blocked: { label: 'Blocked', color: 'bg-red-900/50 text-red-400', icon: Ban },
  closed: { label: 'Closed', color: 'bg-slate-600 text-slate-400', icon: Lock }
};

const SUBJECT_TYPES = {
  sender: { label: 'Sender', icon: Users },
  beneficiary: { label: 'Beneficiary', icon: UserCheck },
  merchant: { label: 'Merchant', icon: Store },
  admin: { label: 'Admin', icon: Shield }
};

const REASON_CODES = [
  { value: 'suspected_fraud', label: 'Suspected Fraud' },
  { value: 'confirmed_fraud', label: 'Confirmed Fraud' },
  { value: 'account_takeover', label: 'Account Takeover' },
  { value: 'unauthorized_activity', label: 'Unauthorized Activity' },
  { value: 'stolen_identity', label: 'Stolen Identity' },
  { value: 'suspicious_pattern', label: 'Suspicious Pattern' },
  { value: 'excessive_verification_failures', label: 'Excessive Verification Failures' },
  { value: 'promotional_abuse', label: 'Promotional Abuse' },
  { value: 'merchant_collusion', label: 'Merchant Collusion' },
  { value: 'qr_misuse', label: 'QR Misuse' },
  { value: 'duplicate_redemption', label: 'Duplicate Redemption' },
  { value: 'multiple_suspicious_accounts', label: 'Multiple Suspicious Accounts' },
  { value: 'chargeback_pattern', label: 'Chargeback Pattern' },
  { value: 'false_beneficiary_info', label: 'False Beneficiary Info' },
  { value: 'false_merchant_info', label: 'False Merchant Info' },
  { value: 'security_violation', label: 'Security Violation' },
  { value: 'tos_violation', label: 'Terms of Service Violation' },
  { value: 'compliance_review', label: 'Compliance Review' },
  { value: 'admin_investigation', label: 'Admin Investigation' },
  { value: 'uat_testing', label: 'UAT / Testing' },
  { value: 'other', label: 'Other (requires notes)' }
];

export default function AdminEnforcementView() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [reviewCases, setReviewCases] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');

  const getToken = () => localStorage.getItem('pecash_token');
  const isAdmin = localStorage.getItem('pecash_admin') === 'true';

  const fetchData = useCallback(async () => {
    const token = getToken();
    
    if (!token || !isAdmin) {
      navigate('/admin');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch dashboard
      const dashboardRes = await fetch(`${API_URL}/api/admin/enforcement/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!dashboardRes.ok) {
        if (dashboardRes.status === 401 || dashboardRes.status === 403) {
          throw new Error('Session expired');
        }
        throw new Error('Failed to fetch enforcement data');
      }
      
      const dashboardData = await dashboardRes.json();
      setDashboard(dashboardData);

      // Fetch subjects with enforcement
      const subjectsRes = await fetch(
        `${API_URL}/api/admin/enforcement/subjects?limit=100${statusFilter ? `&status=${statusFilter}` : ''}${typeFilter ? `&subject_type=${typeFilter}` : ''}${searchQuery ? `&search=${searchQuery}` : ''}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (subjectsRes.ok) {
        const subjectsData = await subjectsRes.json();
        setSubjects(subjectsData.subjects || []);
      }

      // Fetch review cases
      const casesRes = await fetch(`${API_URL}/api/admin/enforcement/review-cases?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (casesRes.ok) {
        const casesData = await casesRes.json();
        setReviewCases(casesData.cases || []);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      if (err.message === 'Session expired') {
        localStorage.removeItem('pecash_token');
        localStorage.removeItem('pecash_admin');
        setTimeout(() => navigate('/admin'), 2000);
      }
    }
  }, [navigate, isAdmin, statusFilter, typeFilter, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    fetchData();
  };

  const openSubjectDetail = async (subjectId, subjectType) => {
    const token = getToken();
    
    try {
      const res = await fetch(
        `${API_URL}/api/admin/enforcement/subjects/${subjectId}?subject_type=${subjectType}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!res.ok) throw new Error('Failed to fetch subject details');
      
      const data = await res.json();
      setSelectedSubject(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading enforcement data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-red-400" />
                Risk & Enforcement Center
              </h1>
              <p className="text-sm text-slate-400">User suspension, blocking & fraud investigation</p>
            </div>
          </div>
          
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="refresh-enforcement"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex justify-between items-center">
            <p className="text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </p>
            <Button variant="ghost" size="sm" onClick={() => setError('')}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Pilot Mode Banner */}
        {dashboard?.pilot_mode && (
          <div className="bg-amber-900/20 border border-amber-800/50 rounded-xl p-4 mb-6">
            <p className="text-amber-300 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              PILOT MODE - Enforcement actions affect simulated accounts only.
            </p>
          </div>
        )}

        {/* Status Summary Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            {Object.entries(STATUS_CONFIG).map(([status, config]) => {
              const count = dashboard.status_summary?.[status] || 0;
              const StatusIcon = config.icon;
              
              return (
                <motion.div
                  key={status}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-slate-800 border border-slate-700 rounded-xl p-4 cursor-pointer hover:border-purple-600 transition-colors ${statusFilter === status ? 'border-purple-500' : ''}`}
                  onClick={() => {
                    setStatusFilter(statusFilter === status ? '' : status);
                    setActiveTab('subjects');
                  }}
                  data-testid={`status-card-${status}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <StatusIcon className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400 text-xs">{config.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{count}</p>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Additional Stats */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-amber-400" />
                <span className="text-slate-400 text-sm">Pending Review Cases</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">{dashboard.pending_review_cases}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Store className="w-4 h-4 text-red-400" />
                <span className="text-slate-400 text-sm">Blocked Merchants</span>
              </div>
              <p className="text-2xl font-bold text-red-400">{dashboard.blocked_merchants}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-blue-400" />
                <span className="text-slate-400 text-sm">Frozen Auth Value (DOP)</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">
                RD${dashboard.frozen_authorization_value_dop?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: Activity },
            { id: 'subjects', label: 'Subjects', icon: Users },
            { id: 'cases', label: 'Review Cases', icon: FileText }
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              className={activeTab === tab.id 
                ? 'bg-purple-600 text-white' 
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Enforcement Actions</h3>
              
              {dashboard.recent_actions?.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No recent enforcement actions</p>
              ) : (
                <div className="space-y-3">
                  {dashboard.recent_actions?.slice(0, 10).map((action, idx) => (
                    <div 
                      key={idx}
                      className="bg-slate-900 rounded-lg p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${STATUS_CONFIG[action.new_status]?.color || 'bg-slate-700'}`}>
                          {(() => {
                            const Icon = STATUS_CONFIG[action.new_status]?.icon || Shield;
                            return <Icon className="w-5 h-5" />;
                          })()}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {action.subject_type}: {action.subject_id.slice(0, 8)}...
                          </p>
                          <p className="text-sm text-slate-400">
                            {action.prior_status} → {action.new_status}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-400">{action.reason_code?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-500">{formatDate(action.performed_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subjects Tab */}
        {activeTab === 'subjects' && (
          <div className="space-y-6">
            {/* Search & Filters */}
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by email, name, ID..."
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white"
                data-testid="status-filter"
              >
                <option value="">All Statuses</option>
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white"
                data-testid="type-filter"
              >
                <option value="">All Types</option>
                {Object.entries(SUBJECT_TYPES).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
              <Button onClick={handleSearch} className="bg-purple-600 hover:bg-purple-700">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>

            {/* Subjects List */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
              {subjects.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No subjects with enforcement status found</p>
              ) : (
                <div className="divide-y divide-slate-700">
                  {subjects.map((subject) => {
                    const statusConfig = STATUS_CONFIG[subject.enforcement_status] || STATUS_CONFIG.active;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <div 
                        key={subject.id}
                        className="p-4 hover:bg-slate-700/50 cursor-pointer"
                        onClick={() => openSubjectDetail(subject.id, subject.role)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusConfig.color}`}>
                              <StatusIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {subject.name || subject.business_name || subject.email}
                              </p>
                              <p className="text-sm text-slate-400">
                                {subject.email} • {subject.role}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 rounded text-xs ${statusConfig.color}`}>
                              {statusConfig.label}
                            </span>
                            <p className="text-xs text-slate-500 mt-1">
                              {formatDate(subject.enforcement_updated_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Review Cases Tab */}
        {activeTab === 'cases' && (
          <div className="space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
              {reviewCases.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No pending review cases</p>
              ) : (
                <div className="divide-y divide-slate-700">
                  {reviewCases.map((caseItem) => (
                    <div key={caseItem.id} className="p-4 hover:bg-slate-700/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">
                            Case: {caseItem.id.slice(0, 8)}...
                          </p>
                          <p className="text-sm text-slate-400">
                            {caseItem.subject_type}: {caseItem.subject_id.slice(0, 8)}... • {caseItem.trigger_reason}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs ${
                            caseItem.status === 'open' ? 'bg-amber-900/50 text-amber-400' :
                            caseItem.status === 'investigating' ? 'bg-blue-900/50 text-blue-400' :
                            caseItem.status === 'action_required' ? 'bg-red-900/50 text-red-400' :
                            'bg-slate-700 text-slate-400'
                          }`}>
                            {caseItem.status?.replace(/_/g, ' ')}
                          </span>
                          <p className="text-xs text-slate-500 mt-1">
                            {formatDate(caseItem.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Subject Detail Modal */}
      <AnimatePresence>
        {selectedSubject && (
          <SubjectDetailModal 
            subject={selectedSubject}
            onClose={() => setSelectedSubject(null)}
            onAction={(action) => {
              setActionType(action);
              setShowActionModal(true);
            }}
            onRefresh={() => {
              openSubjectDetail(selectedSubject.subject.id, selectedSubject.subject.type);
              fetchData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Enforcement Action Modal */}
      <AnimatePresence>
        {showActionModal && selectedSubject && (
          <EnforcementActionModal
            subject={selectedSubject.subject}
            actionType={actionType}
            onClose={() => setShowActionModal(false)}
            onSuccess={() => {
              setShowActionModal(false);
              openSubjectDetail(selectedSubject.subject.id, selectedSubject.subject.type);
              fetchData();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function SubjectDetailModal({ subject, onClose, onAction, onRefresh }) {
  const statusConfig = STATUS_CONFIG[subject.subject.enforcement_status] || STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${statusConfig.color}`}>
              <StatusIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {subject.subject.name || subject.subject.email}
              </h2>
              <p className="text-sm text-slate-400">
                {subject.subject.type} • {subject.subject.email}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Current Status */}
        <div className="bg-slate-900 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Current Status</p>
              <p className={`text-lg font-bold ${statusConfig.color.replace('bg-', 'text-').split(' ')[1]}`}>
                {statusConfig.label}
              </p>
            </div>
            <span className={`px-3 py-1 rounded ${statusConfig.color}`}>
              {subject.subject.enforcement_status}
            </span>
          </div>
        </div>

        {/* Financial Context */}
        <div className="bg-slate-900 rounded-lg p-4 mb-6">
          <h3 className="text-white font-medium mb-3">Financial Context</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-400">Authorizations</p>
              <p className="text-white">{subject.financial_context.authorization_count}</p>
            </div>
            <div>
              <p className="text-slate-400">Outstanding Value (DOP)</p>
              <p className="text-white">RD${subject.financial_context.outstanding_authorization_value_dop?.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Available Actions */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-3">Available Actions</h3>
          <div className="flex flex-wrap gap-2">
            {subject.actions_available.map((action) => (
              <Button
                key={action}
                onClick={() => onAction(action)}
                variant="outline"
                size="sm"
                className={
                  action.includes('block') || action.includes('suspend') || action.includes('close')
                    ? 'border-red-600 text-red-400 hover:bg-red-900/50'
                    : action.includes('restore') || action.includes('unfreeze') || action.includes('remove')
                    ? 'border-emerald-600 text-emerald-400 hover:bg-emerald-900/50'
                    : 'border-amber-600 text-amber-400 hover:bg-amber-900/50'
                }
                data-testid={`action-${action}`}
              >
                {action.replace(/_/g, ' ')}
              </Button>
            ))}
          </div>
        </div>

        {/* Enforcement History */}
        <div>
          <h3 className="text-white font-medium mb-3">Enforcement History</h3>
          {subject.enforcement_history.length === 0 ? (
            <p className="text-slate-400 text-sm">No enforcement history</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {subject.enforcement_history.map((entry, idx) => (
                <div key={idx} className="bg-slate-900 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white">
                      {entry.prior_status} → {entry.new_status}
                    </span>
                    <span className="text-slate-500">{new Date(entry.performed_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-slate-400 text-xs mt-1">
                    {entry.reason_code?.replace(/_/g, ' ')} • By: {entry.performed_by_admin_email?.split('@')[0]}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Warning about money */}
        <div className="mt-6 bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
          <p className="text-blue-300 text-sm flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <strong>Important:</strong> Enforcement actions do NOT move money. Financial disposition (refunds, releases) must be handled through separate authorized workflows.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function EnforcementActionModal({ subject, actionType, onClose, onSuccess }) {
  const [reasonCode, setReasonCode] = useState('');
  const [reasonNotes, setReasonNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Map action type to new status
  const actionStatusMap = {
    'place_under_review': 'under_review',
    'freeze_transactions': 'transactions_frozen',
    'suspend': 'suspended',
    'block': 'blocked',
    'close': 'closed',
    'restore': 'active',
    'unfreeze': 'active',
    'remove_review': 'active'
  };

  const newStatus = actionStatusMap[actionType] || 'active';
  const isDestructive = ['block', 'close', 'suspend'].includes(actionType);

  const handleSubmit = async () => {
    const token = localStorage.getItem('pecash_token');
    setSubmitting(true);
    setError('');

    if (reasonCode === 'other' && !reasonNotes) {
      setError('Notes are required when reason is "Other"');
      setSubmitting(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/enforcement/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject_id: subject.id,
          subject_type: subject.type,
          new_status: newStatus,
          reason_code: reasonCode,
          reason_notes: reasonNotes || null,
          idempotency_key: `${subject.id}-${actionType}-${Date.now()}`
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to perform action');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white capitalize">
            {actionType.replace(/_/g, ' ')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Subject Info */}
        <div className="bg-slate-900 rounded-lg p-4 mb-4">
          <p className="text-slate-400 text-sm">Subject</p>
          <p className="text-white font-medium">{subject.name || subject.email}</p>
          <p className="text-slate-400 text-xs">{subject.type} • {subject.id.slice(0, 8)}...</p>
        </div>

        {/* Action Info */}
        <div className={`rounded-lg p-4 mb-4 ${isDestructive ? 'bg-red-900/20 border border-red-800/50' : 'bg-blue-900/20 border border-blue-800/50'}`}>
          <p className={`text-sm ${isDestructive ? 'text-red-300' : 'text-blue-300'}`}>
            <strong>Action:</strong> {actionType.replace(/_/g, ' ')} → Status will become <strong>{newStatus}</strong>
          </p>
          <p className={`text-xs mt-2 ${isDestructive ? 'text-red-400' : 'text-blue-400'}`}>
            • No funds will be moved, refunded, or confiscated<br/>
            • Financial records will remain preserved<br/>
            • Audit trail will be created
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm block mb-2">Reason *</label>
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              required
              data-testid="reason-select"
            >
              <option value="">Select reason...</option>
              {REASON_CODES.map((reason) => (
                <option key={reason.value} value={reason.value}>{reason.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-slate-400 text-sm block mb-2">
              Notes {reasonCode === 'other' ? '*' : '(optional)'}
            </label>
            <textarea
              value={reasonNotes}
              onChange={(e) => setReasonNotes(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              rows={3}
              placeholder="Additional details..."
              data-testid="reason-notes"
            />
          </div>

          <div className="pt-4 flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !reasonCode}
              className={`flex-1 ${isDestructive ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'}`}
              data-testid="submit-action"
            >
              {submitting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : isDestructive ? (
                <Ban className="w-4 h-4 mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {actionType.replace(/_/g, ' ')}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
